import { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { PaymentMethodPicker } from "@/components/PaymentMethodPicker";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, money } from "@/lib/api";

const LABEL = { open: "Awaiting payment", cash_pending: "Cash reported", paid: "Paid" };

export function DealRoomPayments({ room, onChanged }) {
  const { user } = useAuth();
  const [payInv, setPayInv] = useState(null);
  const [busy, setBusy] = useState(false);
  const list = Array.isArray(room?.invoices) ? room.invoices : [];
  if (!list.length) return <p className="mt-3 text-sm text-slate-400">No payment requests linked.</p>;

  const pay = async (method, note) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/deal-rooms/${room.id}/invoices/${payInv.id}/pay`, { method, note, origin_url: window.location.origin });
      if (data?.checkout_url) { window.location.href = data.checkout_url; return; }
      toast.success("Cash payment reported — your partner will confirm it");
      setPayInv(null);
      onChanged?.();
    } catch (e) { toast.error(errMsg(e), { duration: 9000 }); }
    setBusy(false);
  };
  const confirmCash = async (i) => {
    try { await api.post(`/invoices/${i.id}/confirm-cash`); toast.success("Cash confirmed — receipt emailed"); onChanged?.(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="mt-3 divide-y divide-[#2A1240]/10">
      {list.map((i) => {
        const mine = i.company_id === user?.company_id;
        return (
          <div key={i.id} className="py-3 flex flex-wrap items-center gap-3 text-sm" data-testid={`deal-invoice-${i.id}`}>
            <div className="flex-1 min-w-[10rem]">
              <p className="text-[#2A1240]">{i.number} · {i.title}</p>
              <p className="text-xs text-slate-400">{money(i.amount, 2)}{i.status !== "paid" && i.fee ? ` + ${money(i.fee, 2)} fee by card/crypto` : ""}{i.payment_method ? ` · paid by ${i.payment_method}` : ""}</p>
            </div>
            <span className={`rounded-full px-2.5 py-1 text-[11px] ${i.status === "paid" ? "bg-emerald-400/15 text-emerald-300" : i.status === "cash_pending" ? "bg-amber-300/15 text-amber-200" : "bg-[#2A1240]/10 text-slate-200"}`} data-testid={`deal-invoice-status-${i.id}`}>{LABEL[i.status] || i.status}</span>
            {!mine && i.status === "open" && <button className="btn-gold !py-1.5 !px-4 !text-xs" onClick={() => setPayInv(i)} data-testid={`deal-invoice-pay-${i.id}`}>Pay</button>}
            {mine && i.status === "cash_pending" && <button className="btn-gold !py-1.5 !px-4 !text-xs" onClick={() => confirmCash(i)} data-testid={`deal-invoice-confirm-cash-${i.id}`}>Confirm cash received</button>}
          </div>
        );
      })}
      <Dialog open={!!payInv} onOpenChange={(o) => !o && setPayInv(null)}>
        {payInv && (
          <DialogContent className="bg-[#FFFFFF] border-[#2A1240]/10" data-testid="deal-pay-dialog">
            <DialogTitle className="font-display text-2xl font-normal">Pay {payInv.number}</DialogTitle>
            <DialogDescription className="text-slate-400">{payInv.title} · {money(payInv.amount, 2)} to {payInv.company_name}</DialogDescription>
            <PaymentMethodPicker total={payInv.total} amount={payInv.amount} busy={busy} onPay={pay} />
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
}
